import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import cx from 'clsx';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import PrintIcon from '@material-ui/icons/Print';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Skeleton from 'components/@common/Skeleton';
import ReportLink from './ReportLink';
import GET_CLASSROOM_STATS_QUERY from 'graph/reports/get-classroom-stats';
import GET_ORGANIZATION_STATS_QUERY from 'graph/reports/get-organization-stats';
import GET_STUDENT_STATS_QUERY from 'graph/reports/get-student-stats';
import GET_USER_CLASSROOM_STATS_QUERY from 'graph/reports/get-user-classroom-stats';
import {
  Classroom,
  ClassroomLicenseUsage,
  Organization,
  OrganizationLicenseUsage,
  OrganizationType,
  ProductType,
  Student,
  User
} from 'types/schema-generated-types';
import { ReportEntityType, ReportParams, ReportProduct } from '../../types';
import { resolveDateRange } from '../../utils/report-date-utils';
import { getEntityType, PRODUCT_FAMILY_OPTIONS_MAP } from '../../utils/report-param-utils';
import useReportQueryParams from '../../hooks/use-report-query-params';
import BreakableTypography from 'components/@common/BreakableTypography';
import ReportExportButton from './ReportExportButton';
import ReportExportButtonAsync from './ReportExportButtonAsync';
import EXPORT_REPORT_MAP from 'components/ReportsModule/export';
import { usePrint } from 'components/ReportsModule/providers/PrintProvider';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3)
    },
    skeleton: {
      margin: theme.spacing(2),
      marginLeft: theme.spacing(3),
      margingRight: theme.spacing(3)
    },
    divider: {
      marginRight: theme.spacing(-3),
      marginLeft: theme.spacing(-3)
    },
    titleContainer: {
      marginBottom: theme.spacing(2)
    },
    statsContainer: {
      justifyContent: 'flex-end',
      flexWrap: 'nowrap',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'flex-start',
        flexWrap: 'wrap'
      }
    },
    statItem: {
      border: `1px solid #C3C3C3`,
      borderRadius: 4,
      padding: theme.spacing(1),
      margin: theme.spacing(0.5),
      minWidth: 140
      //'&:first-child': { marginLeft: 0 }
    },
    statText: {},
    chip: {
      height: 16,
      textTransform: 'uppercase',
      fontWeight: 400,
      fontSize: 12,
      borderRadius: 4,
      color: 'white',
      marginLeft: theme.spacing(1)
    },
    detailContainer: {
      marginTop: theme.spacing(2),
      [theme.breakpoints.up('lg')]: {
        flexWrap: 'nowrap'
      }
    },
    detail: {
      [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(1)
      }
    },
    detailHeader: { fontWeight: 600 },
    detailText: { color: theme.palette.grey[700] },
    noPrint: {
      '@media print': { display: 'none' }
    }
  };
});

interface Entity {
  name: string;
  subtitle?: React.ReactNode;
  type?: string;
  typeColor?: string;
  stats: EntityStats[];
}

interface EntityStats {
  id: string;
  name: string;
  value: string | number;
}

const ENTITY_CHIP_COLORS = {
  [ReportEntityType.DISTRICT]: '#BB6577',
  [ReportEntityType.SCHOOL]: '#444345',
  [ReportEntityType.USER_GROUP]: '#A165B7',
  [ReportEntityType.USER]: '#F4A520',
  [ReportEntityType.CLASSROOM]: '#72BB66',
  [ReportEntityType.STUDENT]: '#95A8B5'
};

const createReportLink = (
  entityId?: number,
  entityType?: ReportEntityType,
  name?: string,
  organizationId?: number
): React.ReactNode => {
  return (
    <ReportLink
      key={entityId}
      entityType={entityType}
      entityId={entityId}
      organizationId={organizationId}
    >
      {name}
    </ReportLink>
  );
};

const getAssignedLicenseCount = (
  productFamilyCode: ReportProduct,
  licenseUsage: Array<ClassroomLicenseUsage | OrganizationLicenseUsage> | null | undefined
): number => {
  return (
    licenseUsage?.reduce((total, usage) => {
      if (
        usage?.product.family.code !== productFamilyCode ||
        usage?.product.type !== ProductType.STUDENT
      )
        return total;
      return total + (usage?.assignedCount ?? 0);
    }, 0) ?? 0
  );
};

const parseClassroomQueryResult = (
  { classroom }: { classroom: Classroom },
  reportParams: ReportParams
): Entity => {
  const { licenseUsage, name, organization, stats } = classroom;
  const { studentCount, userCount } = stats;

  const assignedLicenseCount = getAssignedLicenseCount(
    reportParams.productFamilyCode,
    licenseUsage
  );

  return {
    name,
    subtitle: createReportLink(
      organization?.id,
      getEntityType(organization?.type),
      organization?.name
    ),
    stats: [
      { id: 'students', name: 'Students', value: studentCount },
      { id: 'educators', name: 'Educators', value: userCount },
      { id: 'licenses', name: 'Assigned Licenses', value: assignedLicenseCount }
    ]
  };
};

const parseOrgQueryResult = (
  { organization }: { organization: Organization },
  reportParams: ReportParams
): Entity => {
  const { licenseUsage, name, parentOrganization, subOrganizations: subOrgs, stats } = organization;
  const { classroomCount, classroomStudentCount, userCount } = stats;
  const isDistrictReport = reportParams.entityType === ReportEntityType.DISTRICT;
  const schools = subOrgs?.filter(subOrg => subOrg?.type === OrganizationType.SCHOOL) ?? [];

  const assignedLicenseCount = getAssignedLicenseCount(
    reportParams.productFamilyCode,
    licenseUsage
  );

  return {
    name,
    subtitle: createReportLink(
      parentOrganization?.id,
      getEntityType(parentOrganization?.type),
      parentOrganization?.name
    ),
    stats: [
      ...(isDistrictReport ? [{ id: 'schools', name: 'Schools', value: schools.length }] : []),
      { id: 'classes', name: 'Classes', value: classroomCount },
      { id: 'students', name: 'Students', value: classroomStudentCount },
      { id: 'educators', name: 'Educators', value: userCount },
      { id: 'licenses', name: 'Assigned Licenses', value: assignedLicenseCount }
    ]
  };
};

const parseUserQueryResult = ({ user }: { user: User }, reportParams: ReportParams): Entity => {
  const { classroomEnrollments } = user;
  const classrooms = classroomEnrollments?.map(enrollment => enrollment.classroom) ?? [];

  const { studentCount, assignedLicenseCount } = classrooms.reduce(
    (acc, classroom: Classroom) => {
      const studentCount = classroom.stats.studentCount;

      const assignedLicenseCount = getAssignedLicenseCount(
        reportParams.productFamilyCode,
        classroom.licenseUsage
      );

      acc.studentCount += studentCount;
      acc.assignedLicenseCount += assignedLicenseCount;
      return acc;
    },
    {
      studentCount: 0,
      assignedLicenseCount: 0
    }
  );

  const organization = user.organizationAssociations?.[0]?.organization;

  return {
    name: `${user.firstName} ${user.lastName}`,
    subtitle:
      organization &&
      createReportLink(organization.id, getEntityType(organization.type), organization.name),
    stats: [
      { id: 'classes', name: 'Classes', value: classrooms.length },
      { id: 'students', name: 'Students', value: studentCount },
      { id: 'licenses', name: 'Assigned Licenses', value: assignedLicenseCount }
    ]
  };
};

const parseStudentQueryResult = ({ student }: { student: Student }): Entity => {
  const { classrooms, organization } = student;

  // TODO: Filter this in the query?
  const activeClassrooms = classrooms?.filter(classroom => !classroom.dateArchived);

  const subtitle: any[] = [
    ...(organization?.rootOrganization && organization?.rootOrganization.id !== organization.id
      ? [
          createReportLink(
            organization.rootOrganization.id,
            getEntityType(organization.rootOrganization.type),
            organization.rootOrganization?.name
          )
        ]
      : []),
    createReportLink(organization?.id, getEntityType(organization?.type), organization?.name),
    [
      ...(activeClassrooms?.map((classroom, i) => [
        i > 0 && ', ',
        createReportLink(
          classroom?.id,
          ReportEntityType.CLASSROOM,
          classroom?.name,
          classroom?.organizationId
        )
      ]) ?? [])
    ]
  ].map((item, i) => [i > 0 && ' - ', item]);

  return {
    name: `${student.firstName} ${student.lastName}`,
    subtitle,
    stats: []
  };
};

const ENTITY_MAP: { [key in ReportEntityType]?: any } = {
  [ReportEntityType.CLASSROOM]: {
    query: GET_CLASSROOM_STATS_QUERY,
    getQueryVariables: (entityId: number) => ({ id: entityId }),
    parseQueryData: parseClassroomQueryResult
  },
  [ReportEntityType.DISTRICT]: {
    query: GET_ORGANIZATION_STATS_QUERY,
    getQueryVariables: (entityId: number) => ({ id: entityId }),
    parseQueryData: parseOrgQueryResult
  },
  [ReportEntityType.SCHOOL]: {
    query: GET_ORGANIZATION_STATS_QUERY,
    getQueryVariables: (entityId: number) => ({ id: entityId }),
    parseQueryData: parseOrgQueryResult
  },
  [ReportEntityType.STUDENT]: {
    query: GET_STUDENT_STATS_QUERY,
    getQueryVariables: (entityId: number) => ({ id: entityId }),
    parseQueryData: parseStudentQueryResult
  },
  [ReportEntityType.USER_GROUP]: {
    query: GET_ORGANIZATION_STATS_QUERY,
    getQueryVariables: (entityId: number) => ({ id: entityId }),
    parseQueryData: parseOrgQueryResult
  },
  [ReportEntityType.USER]: {
    query: GET_USER_CLASSROOM_STATS_QUERY,
    getQueryVariables: (entityId: number) => ({ id: entityId }),
    parseQueryData: parseUserQueryResult
  }
};

const ReportHeader = (): React.ReactElement => {
  const [reportParams] = useReportQueryParams();
  const { productFamilyCode, entityId, entityType, dateRange } = reportParams;
  const classes = useStyles();
  const { t } = useTranslation('Reports');
  const reportWorkbook = EXPORT_REPORT_MAP[productFamilyCode]?.[entityType];

  const { dateStart, dateEnd } = useMemo(() => resolveDateRange(dateRange), [dateRange]);
  const { query, getQueryVariables, parseQueryData } = ENTITY_MAP[entityType];
  const [fetchReportDetails, { data, loading }] = useLazyQuery(query);
  const [, { handlePrint }] = usePrint();

  useEffect(() => {
    fetchReportDetails({ variables: getQueryVariables(entityId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType, dateRange]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const entity: Entity = useMemo(() => (data ? parseQueryData(data, reportParams) : null), [data]);

  return (
    <Paper component="header" className={classes.container}>
      {!entity || loading ? (
        <Skeleton height={120} className={classes.skeleton} />
      ) : (
        <Grid container className={classes.titleContainer}>
          <Grid item xs={12} lg>
            <BreakableTypography variant="h2">
              {entity.name}
              <Chip
                size="small"
                data-testid="report-entity-type"
                label={t(entityType)}
                className={classes.chip}
                style={{ backgroundColor: ENTITY_CHIP_COLORS[entityType] }}
              />
            </BreakableTypography>
            {entity.subtitle && <Typography variant="subtitle1">{entity.subtitle}</Typography>}
          </Grid>
          {entity.stats && (
            <Grid container item xs={12} lg className={classes.statsContainer}>
              {entity.stats.map(({ id, name, value }) => (
                <Grid key={id} item className={classes.statItem}>
                  <Typography component="h3" variant="body2" noWrap>
                    {t(name)}
                  </Typography>
                  <Typography component="span" variant="h3" className={classes.statText}>
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      )}
      <Divider className={classes.divider} />
      <Grid item container spacing={2} className={classes.detailContainer}>
        <Grid item xs={12} lg className={classes.detail}>
          <Typography className={cx(classes.detailHeader, classes.detailText)} variant="subtitle2">
            {t('Report Product')}
          </Typography>
          <Typography className={classes.detailText}>
            {t(PRODUCT_FAMILY_OPTIONS_MAP[productFamilyCode]?.value, { ns: 'ProductFamily' })}
          </Typography>
        </Grid>
        <Grid item xs={12} lg className={classes.detail}>
          <Typography className={cx(classes.detailHeader, classes.detailText)} variant="subtitle2">
            {t('Report Date Range')}
          </Typography>
          <Typography className={classes.detailText}>
            {`From ${dateStart.toLocaleDateString('en-US')} to ${dateEnd.toLocaleDateString(
              'en-US'
            )}`}
          </Typography>
        </Grid>
        <Grid item xs={12} lg className={classes.detail}>
          <Typography className={cx(classes.detailHeader, classes.detailText)} variant="subtitle2">
            {t('Report Generation Date')}
          </Typography>
          <Typography className={classes.detailText}>
            {new Date().toLocaleDateString('en-US')}
          </Typography>
        </Grid>
        {/*
        <Grid item xs={12} md>
          <Typography className={classes.detailText} variant="subtitle2">
            {t('Content Displayed By')}
          </Typography>
          <Typography className={classes.detailText}>{displayBy}</Typography>
        </Grid>
        */}
        <Divider flexItem orientation="vertical" variant="middle" className={classes.noPrint} />
        <Grid item xs={12} lg="auto" className={classes.noPrint}>
          <Grid container spacing={2} justify="flex-end">
            <Grid item>
              {reportWorkbook &&
                (reportWorkbook?.async ? (
                  <ReportExportButtonAsync workbook={reportWorkbook} />
                ) : (
                  <ReportExportButton workbook={reportWorkbook} />
                ))}
            </Grid>
            <Grid item>
              <Button
                onClick={() => handlePrint()}
                variant="outlined"
                color="secondary"
                disableElevation
                startIcon={<PrintIcon />}
              >
                {t('Print Report')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ReportHeader;
